import { useEffect, useRef, useState } from 'react';
import { X, Move, Plus } from 'lucide-react';

/**
 * PlantillaCanvas — versión del canvas para diseño de plantillas.
 *
 * Diferencias con PaginaCanvas:
 *   - Trabaja con state local (bloques), no persiste cada cambio al backend.
 *     El padre recibe los cambios por onCambio() y decide cuándo guardarlos.
 *   - Los bloques no llevan contenido concreto (sin articuloId/pautaId) —
 *     son SLOTS con tipo y etiqueta descriptiva.
 *   - Nuevos bloques se agregan desde botones "+ Artículo", "+ Pauta",
 *     "+ Fijo" en la barra lateral izquierda del canvas.
 */
export default function PlantillaCanvas({ plantilla, onCambio }) {
  const bloques = plantilla.bloques;
  const contenedorRef = useRef(null);
  const [dims, setDims] = useState({ ancho: 0, alto: 0, celdaAncho: 0, celdaAlto: 0 });

  // Proporción tabloide 28×43 cm
  useEffect(() => {
    const compute = () => {
      const el = contenedorRef.current;
      if (!el) return;
      const anchoDisp = el.clientWidth;
      const altoDisp  = el.clientHeight;
      const ratio = 28 / 43;
      let ancho = anchoDisp, alto = anchoDisp / ratio;
      if (alto > altoDisp) { alto = altoDisp; ancho = altoDisp * ratio; }
      setDims({
        ancho, alto,
        celdaAncho: ancho / plantilla.columnas,
        celdaAlto:  alto  / plantilla.filas,
      });
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (contenedorRef.current) ro.observe(contenedorRef.current);
    return () => ro.disconnect();
  }, [plantilla.columnas, plantilla.filas]);

  // ── Agregar bloque nuevo ─────────────────────────────────────────
  // Encuentra el primer hueco libre en el grid (top-left) del tamaño
  // por defecto solicitado. Si nada cabe, lo pone en (1,1) igual y el
  // usuario lo moverá.
  const agregarBloque = (tipoSlot, cs = 2, fs = 3) => {
    let colElegida = 1, filaElegida = 1;
    outer: for (let f = 1; f <= plantilla.filas - fs + 1; f++) {
      for (let c = 1; c <= plantilla.columnas - cs + 1; c++) {
        const hayColision = bloques.some(b =>
          c < b.columnaInicio + b.columnasSpan &&
          b.columnaInicio < c + cs &&
          f < b.filaInicio + b.filasSpan &&
          b.filaInicio < f + fs
        );
        if (!hayColision) { colElegida = c; filaElegida = f; break outer; }
      }
    }
    const nuevo = {
      _key: `local-${Date.now()}`,
      columnaInicio: colElegida, filaInicio: filaElegida,
      columnasSpan: cs, filasSpan: fs,
      tipoSlot,
      etiqueta: null,
    };
    onCambio([...bloques, nuevo]);
  };

  // ── Drag para mover / resize (en memoria) ─────────────────────────
  const [drag, setDrag] = useState(null);

  const iniciarMove = (e, b) => {
    e.stopPropagation();
    setDrag({ key: b._key, tipo: 'move', startX: e.clientX, startY: e.clientY,
              orig: { col: b.columnaInicio, fila: b.filaInicio, cs: b.columnasSpan, fs: b.filasSpan } });
  };
  const iniciarResize = (e, b) => {
    e.stopPropagation();
    setDrag({ key: b._key, tipo: 'resize', startX: e.clientX, startY: e.clientY,
              orig: { col: b.columnaInicio, fila: b.filaInicio, cs: b.columnasSpan, fs: b.filasSpan } });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const deltaCol  = Math.round(dx / dims.celdaAncho);
      const deltaFila = Math.round(dy / dims.celdaAlto);
      const nuevos = bloques.map(b => {
        if (b._key !== drag.key) return b;
        if (drag.tipo === 'move') {
          const c = Math.max(1, Math.min(plantilla.columnas - drag.orig.cs + 1, drag.orig.col + deltaCol));
          const f = Math.max(1, Math.min(plantilla.filas    - drag.orig.fs + 1, drag.orig.fila + deltaFila));
          return { ...b, columnaInicio: c, filaInicio: f };
        } else {
          const cs = Math.max(1, Math.min(plantilla.columnas - drag.orig.col + 1, drag.orig.cs + deltaCol));
          const fs = Math.max(1, Math.min(plantilla.filas    - drag.orig.fila + 1, drag.orig.fs + deltaFila));
          return { ...b, columnasSpan: cs, filasSpan: fs };
        }
      });
      onCambio(nuevos);
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, dims, plantilla.columnas, plantilla.filas, bloques]);

  const eliminarBloque = (key) => onCambio(bloques.filter(b => b._key !== key));

  const cambiarEtiqueta = (key, etiqueta) =>
    onCambio(bloques.map(b => b._key === key ? { ...b, etiqueta } : b));

  const cambiarTipoSlot = (key, tipoSlot) =>
    onCambio(bloques.map(b => b._key === key ? { ...b, tipoSlot } : b));

  return (
    <div style={{ display:'flex', gap:12, height:'100%' }}>
      {/* ─── Barra lateral: botones para agregar bloques ─── */}
      <div style={{
        width:130, display:'flex', flexDirection:'column', gap:6,
        background:'#fff', border:'1px solid #d1d5db', borderRadius:4, padding:8,
      }}>
        <div style={{ fontSize:11, color:'#666', fontWeight:600, marginBottom:4 }}>Añadir bloque</div>
        <button onClick={() => agregarBloque('articulo')} style={{ ...btnAdd, borderColor:'#1B4332', color:'#1B4332' }}>
          <Plus size={12} /> Artículo
        </button>
        <button onClick={() => agregarBloque('pauta')} style={{ ...btnAdd, borderColor:'#1e40af', color:'#1e40af' }}>
          <Plus size={12} /> Pauta
        </button>
        <button onClick={() => agregarBloque('elemento_fijo', 6, 1)} style={{ ...btnAdd, borderColor:'#78350f', color:'#78350f' }}>
          <Plus size={12} /> Elemento fijo
        </button>
        <div style={{ fontSize:10, color:'#888', marginTop:8, lineHeight:1.3 }}>
          Los bloques aparecen en el primer hueco libre del grid. Muévelos y redimensiónalos con el drag.
        </div>
      </div>

      {/* ─── Canvas ─── */}
      <div ref={contenedorRef} style={{
        flex:1, display:'flex', alignItems:'center', justifyContent:'center',
      }}>
        <div style={{
          position:'relative',
          width: dims.ancho || '100%',
          height: dims.alto || '100%',
          background:'#fff',
          boxShadow:'0 4px 24px rgba(0,0,0,0.15)',
          backgroundImage: `
            linear-gradient(to right, #f0f0f0 1px, transparent 1px),
            linear-gradient(to bottom, #f0f0f0 1px, transparent 1px)
          `,
          backgroundSize: `${dims.celdaAncho}px ${dims.celdaAlto}px`,
        }}>
          {bloques.map(b => (
            <SlotVisual
              key={b._key}
              bloque={b}
              celdaAncho={dims.celdaAncho}
              celdaAlto={dims.celdaAlto}
              onMove={(e) => iniciarMove(e, b)}
              onResize={(e) => iniciarResize(e, b)}
              onEliminar={() => eliminarBloque(b._key)}
              onEtiqueta={(v) => cambiarEtiqueta(b._key, v)}
              onTipoSlot={(v) => cambiarTipoSlot(b._key, v)}
            />
          ))}
          {bloques.length === 0 && (
            <div style={{
              position:'absolute', inset:0, display:'flex', alignItems:'center',
              justifyContent:'center', color:'#aaa', fontSize:13, textAlign:'center', padding:20,
            }}>
              Canvas vacío. Añade bloques desde la barra izquierda para diseñar el layout de esta plantilla.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SlotVisual({ bloque, celdaAncho, celdaAlto, onMove, onResize, onEliminar, onEtiqueta, onTipoSlot }) {
  const colores = {
    articulo:       { bg:'#f0fdf4', border:'#1B4332', color:'#1B4332' },
    pauta:          { bg:'#eff6ff', border:'#1e40af', color:'#1e40af' },
    elemento_fijo:  { bg:'#fef3c7', border:'#78350f', color:'#78350f' },
  };
  const c = colores[bloque.tipoSlot] || colores.articulo;

  return (
    <div style={{
      position:'absolute',
      left: (bloque.columnaInicio - 1) * celdaAncho,
      top:  (bloque.filaInicio    - 1) * celdaAlto,
      width:  bloque.columnasSpan * celdaAncho,
      height: bloque.filasSpan    * celdaAlto,
      background: c.bg, border: `2px solid ${c.border}`,
      overflow:'hidden', boxSizing:'border-box',
      display:'flex', flexDirection:'column',
    }}>
      <div
        onMouseDown={onMove}
        style={{
          padding:'2px 6px', background:'rgba(0,0,0,0.05)',
          cursor:'grab', display:'flex', alignItems:'center', gap:6,
          fontSize:10, color:c.color, userSelect:'none',
        }}
      >
        <Move size={10} />
        <select
          value={bloque.tipoSlot}
          onChange={(e) => onTipoSlot(e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
          style={{ fontSize:10, padding:'0 2px', border:'none', background:'transparent', color:c.color, fontWeight:600, cursor:'pointer' }}
        >
          <option value="articulo">Artículo</option>
          <option value="pauta">Pauta</option>
          <option value="elemento_fijo">Fijo</option>
        </select>
        <span style={{ flex:1, color:'#888', fontSize:9 }}>{bloque.columnasSpan}×{bloque.filasSpan}</span>
        <button onClick={(e) => { e.stopPropagation(); onEliminar(); }}
          style={{ background:'transparent', border:'none', cursor:'pointer', color:'#dc2626', padding:0 }}>
          <X size={11} />
        </button>
      </div>
      <div style={{ flex:1, padding:6, display:'flex', alignItems:'center', justifyContent:'center', minHeight:0 }}>
        <input
          type="text"
          placeholder="Etiqueta (ej. Hero principal)"
          value={bloque.etiqueta || ''}
          onChange={(e) => onEtiqueta(e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            width:'90%', textAlign:'center', border:'none', background:'transparent',
            fontSize:11, color:c.color, fontWeight:600, outline:'none',
          }}
        />
      </div>
      <div
        onMouseDown={onResize}
        style={{
          position:'absolute', right:0, bottom:0, width:12, height:12,
          background: c.border, cursor:'nwse-resize',
        }}
        title="Arrastra para redimensionar"
      />
    </div>
  );
}

const btnAdd = {
  background:'#fff', border:'1px solid', borderRadius:4,
  padding:'6px 8px', fontSize:11, fontWeight:600, cursor:'pointer',
  display:'inline-flex', alignItems:'center', gap:4, justifyContent:'flex-start',
};
